import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams, Link } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import {
  usePlayerProfile,
  usePlayerPlans,
  usePlayerVideos,
  usePlayerNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
} from '@/hooks/use-coach';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Calendar,
  Video,
  FileText,
  Plus,
  Edit2,
  Trash2,
  Play,
  Eye,
  ChevronUp,
} from 'lucide-react';
import type { TrainingPlan, ExerciseVideoFeedback, CoachNote } from '@shared/schema';
import { ExerciseModal } from '@/components/exercise-modal';
import type { GeneratedPlan, TrainingPlanDay } from '@/types';
import { trackEvent } from '@/lib/analytics/posthog';

export default function PlayerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const playerId = id ? parseInt(id, 10) : undefined;
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const [, setLocation] = useLocation();

  const { data: profile, isLoading: profileLoading } = usePlayerProfile(playerId);
  const { data: plans, isLoading: plansLoading } = usePlayerPlans(playerId);
  const { data: videos, isLoading: videosLoading } = usePlayerVideos(playerId);
  const { data: notes, isLoading: notesLoading } = usePlayerNotes(playerId);

  const createNoteMutation = useCreateNote();
  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();

  const [activeTab, setActiveTab] = useState('plans');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteTargetType, setNoteTargetType] = useState<'plan' | 'video'>('plan');
  const [noteTargetId, setNoteTargetId] = useState<number | undefined>();
  const [editingNote, setEditingNote] = useState<CoachNote | null>(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setLocation('/login');
      return;
    }

    if (!authLoading && user?.accountType !== 'coach') {
      setLocation('/');
      return;
    }
  }, [isAuthenticated, authLoading, user, setLocation]);

  // Track coach viewing a player's profile (wait for all data to load)
  const viewTracked = useRef(false);
  useEffect(() => {
    if (profile && playerId && !plansLoading && !videosLoading && !viewTracked.current) {
      viewTracked.current = true;
      trackEvent('coach_player_viewed', {
        player_id: playerId,
        plans_count: plans?.length ?? 0,
        videos_count: videos?.length ?? 0,
      });
    }
  }, [profile, playerId, plansLoading, videosLoading, plans, videos]);

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading player...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== 'coach') {
    return null;
  }

  if (!profile) {
    return (
      <div className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Player Not Found</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              This player could not be found or is not on your team.
            </p>
            <Link href="/coach/dashboard">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string | Date) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  const formatTime = (dateString: string | Date) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const handleAddNote = () => {
    if (!playerId || !noteContent.trim() || !noteTargetId) return;

    createNoteMutation.mutate(
      {
        athleteUserId: playerId,
        targetType: noteTargetType,
        targetId: noteTargetId,
        content: noteContent.trim(),
      },
      {
        onSuccess: () => {
          trackEvent('coach_note_created', {
            player_id: playerId,
            target_type: noteTargetType,
            note_length: noteContent.trim().length,
          });
          setNoteContent('');
          setIsAddingNote(false);
          setNoteTargetId(undefined);
        },
      }
    );
  };

  const handleUpdateNote = () => {
    if (!editingNote || !editNoteContent.trim()) return;

    updateNoteMutation.mutate(
      {
        noteId: editingNote.id,
        content: editNoteContent.trim(),
      },
      {
        onSuccess: () => {
          setEditingNote(null);
          setEditNoteContent('');
        },
      }
    );
  };

  const handleDeleteNote = () => {
    if (!noteToDelete) return;

    deleteNoteMutation.mutate(noteToDelete, {
      onSuccess: () => {
        trackEvent('coach_note_deleted', {
          note_id: noteToDelete,
          player_id: playerId,
        });
        setNoteToDelete(null);
      },
    });
  };

  const startAddingNoteFor = (targetType: 'plan' | 'video', targetId: number) => {
    setNoteTargetType(targetType);
    setNoteTargetId(targetId);
    setNoteContent('');
    setIsAddingNote(true);
  };

  const getNotesForTarget = (targetType: 'plan' | 'video', targetId: number) => {
    if (!notes) return [];
    return notes.filter((n) => n.targetType === targetType && n.targetId === targetId);
  };

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-[var(--light-1)] to-[var(--light-2)] dark:from-[var(--dark-1)] dark:to-[var(--dark-2)]">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <Link href="/coach/dashboard">
            <Button variant="ghost" className="mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>

          {/* Player Header */}
          <div className="flex items-start gap-6 mb-8">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[var(--apple-green)] to-[var(--apple-blue)] flex items-center justify-center text-white text-2xl font-bold shrink-0">
              {profile.user.name?.[0]?.toUpperCase() || profile.user.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-1">{profile.user.name || 'Unnamed Player'}</h1>
              <p className="text-gray-600 dark:text-gray-300 mb-2">{profile.user.email}</p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {formatDate(profile.membership.joinedAt)}
                </span>
              </div>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid grid-cols-3 w-full max-w-lg glass-card dark:glass-card-dark rounded-2xl p-1">
              <TabsTrigger value="plans" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Plans</span>
              </TabsTrigger>
              <TabsTrigger value="videos" className="flex items-center gap-2">
                <Video className="w-4 h-4" />
                <span className="hidden sm:inline">Videos</span>
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Notes</span>
              </TabsTrigger>
            </TabsList>

            {/* Plans Tab */}
            <TabsContent value="plans" className="space-y-4">
              {plansLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Loading plans...</p>
                </div>
              ) : !plans || plans.length === 0 ? (
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Training Plans</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      This player hasn't created any training plans since joining your team.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {plans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      notes={getNotesForTarget('plan', plan.id)}
                      onAddNote={() => startAddingNoteFor('plan', plan.id)}
                      onEditNote={(note) => {
                        setEditingNote(note);
                        setEditNoteContent(note.content);
                      }}
                      onDeleteNote={(noteId) => setNoteToDelete(noteId)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos" className="space-y-4">
              {videosLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Loading videos...</p>
                </div>
              ) : !videos || videos.length === 0 ? (
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardContent className="py-12 text-center">
                    <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Videos</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      This player hasn't uploaded any exercise videos since joining your team.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {videos.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      notes={getNotesForTarget('video', video.id)}
                      onAddNote={() => startAddingNoteFor('video', video.id)}
                      onEditNote={(note) => {
                        setEditingNote(note);
                        setEditNoteContent(note.content);
                      }}
                      onDeleteNote={(noteId) => setNoteToDelete(noteId)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* All Notes Tab */}
            <TabsContent value="notes" className="space-y-4">
              {notesLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300">Loading notes...</p>
                </div>
              ) : !notes || notes.length === 0 ? (
                <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Notes</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      You haven't added any notes for this player yet. Go to the Plans or Videos tab to add notes on specific content.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {notes.map((note) => (
                    <Card key={note.id} className="glass-card dark:glass-card-dark rounded-3xl shadow-lg">
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline" className="text-xs">
                                {note.targetType === 'plan' && `Plan #${note.targetId}`}
                                {note.targetType === 'video' && `Video #${note.targetId}`}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {formatDate(note.createdAt)} at {formatTime(note.createdAt)}
                              </span>
                            </div>
                            <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{note.content}</p>
                          </div>
                          <div className="flex items-center gap-1 ml-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingNote(note);
                                setEditNoteContent(note.content);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20"
                              onClick={() => setNoteToDelete(note.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Add Note Dialog */}
      <AlertDialog open={isAddingNote} onOpenChange={setIsAddingNote}>
        <AlertDialogContent className="glass-card dark:glass-card-dark rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Add Coach Note</AlertDialogTitle>
            <AlertDialogDescription>
              Add a note about this player's {noteTargetType} #{noteTargetId}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Enter your observation or feedback..."
            rows={4}
            className="mt-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-2xl"
              onClick={handleAddNote}
              disabled={!noteContent.trim() || createNoteMutation.isPending}
            >
              {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Note Dialog */}
      <AlertDialog open={!!editingNote} onOpenChange={() => setEditingNote(null)}>
        <AlertDialogContent className="glass-card dark:glass-card-dark rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Note</AlertDialogTitle>
          </AlertDialogHeader>
          <Textarea
            value={editNoteContent}
            onChange={(e) => setEditNoteContent(e.target.value)}
            placeholder="Enter your observation or feedback..."
            rows={4}
            className="mt-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-2xl"
              onClick={handleUpdateNote}
              disabled={!editNoteContent.trim() || updateNoteMutation.isPending}
            >
              {updateNoteMutation.isPending ? 'Saving...' : 'Save Changes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Note Confirmation */}
      <AlertDialog open={!!noteToDelete} onOpenChange={() => setNoteToDelete(null)}>
        <AlertDialogContent className="glass-card dark:glass-card-dark rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600 rounded-2xl"
              onClick={handleDeleteNote}
              disabled={deleteNoteMutation.isPending}
            >
              {deleteNoteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

// Note Card Component
function NoteCard({
  note,
  onEdit,
  onDelete,
}: {
  note: CoachNote;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const formatDate = (dateString: string | Date) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <div className="glass-card dark:glass-card-dark rounded-2xl p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{note.content}</p>
          <p className="text-xs text-gray-500 mt-2">{formatDate(note.createdAt)}</p>
        </div>
        <div className="flex items-center gap-1 ml-4">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20"
            onClick={onDelete}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Day Card Component for expanded plan view
function DayCard({
  day,
  onExerciseClick,
}: {
  day: TrainingPlanDay;
  onExerciseClick: (exercise: any) => void;
}) {
  const isRestDay = day.training_focus === "Recovery Day" || day.theme === "Rest Day";

  return (
    <div className="glass-card dark:glass-card-dark rounded-2xl p-3 border border-gray-200 dark:border-gray-700">
      {/* Day header */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-gray-800 to-gray-600 flex items-center justify-center">
          <span className="text-white font-bold text-xs">{day.day}</span>
        </div>
        <div>
          <p className="font-semibold text-sm">Day {day.day}</p>
          <p className="text-xs text-gray-500 truncate">{day.training_focus || day.theme}</p>
        </div>
      </div>

      {isRestDay ? (
        <div className="text-center py-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
          <span className="text-xl">😴</span>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">Recovery</p>
        </div>
      ) : (
        <div className="space-y-2">
          {day.warm_up && day.warm_up.length > 0 && (
            <ExerciseSectionDisplay
              title="Warm-up"
              exercises={day.warm_up}
              color="green"
              onExerciseClick={onExerciseClick}
            />
          )}
          {day.main_training && day.main_training.length > 0 && (
            <ExerciseSectionDisplay
              title="Main"
              exercises={day.main_training}
              color="orange"
              onExerciseClick={onExerciseClick}
            />
          )}
          {day.stretching && day.stretching.length > 0 && (
            <ExerciseSectionDisplay
              title="Stretch"
              exercises={day.stretching}
              color="gray"
              onExerciseClick={onExerciseClick}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Exercise Section Component
function ExerciseSectionDisplay({
  title,
  exercises,
  color,
  onExerciseClick,
}: {
  title: string;
  exercises: any[];
  color: 'green' | 'orange' | 'gray';
  onExerciseClick: (exercise: any) => void;
}) {
  const borderColors = {
    green: 'border-l-[var(--apple-green)]',
    orange: 'border-l-[var(--apple-orange)]',
    gray: 'border-l-gray-400',
  };

  return (
    <div className={`border-l-2 ${borderColors[color]} pl-2`}>
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">{title}</p>
      <div className="space-y-0.5">
        {exercises.slice(0, 3).map((exercise, idx) => (
          <button
            key={idx}
            onClick={() => onExerciseClick(exercise)}
            className="text-xs text-left hover:text-[var(--apple-green)] transition-colors block truncate w-full"
          >
            • {exercise.exercise}
            {exercise.sets && ` (${exercise.sets}×${exercise.reps || exercise.duration})`}
          </button>
        ))}
        {exercises.length > 3 && (
          <p className="text-[10px] text-gray-400">+{exercises.length - 3} more</p>
        )}
      </div>
    </div>
  );
}

// Plan Card Component
function PlanCard({
  plan,
  notes,
  onAddNote,
  onEditNote,
  onDeleteNote,
}: {
  plan: TrainingPlan;
  notes: CoachNote[];
  onAddNote: () => void;
  onEditNote: (note: CoachNote) => void;
  onDeleteNote: (noteId: number) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [exerciseModalOpen, setExerciseModalOpen] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<any>(null);

  // Get plan data with proper type
  const planData = plan.planData as GeneratedPlan | undefined;

  // Get display title (use plan name or fallback)
  const displayTitle = plan.name || 'Training Plan';

  // Calculate training days count
  const trainingDays = planData?.days?.filter(d =>
    d.training_focus !== "Recovery Day" && d.theme !== "Rest Day"
  ).length || 0;

  const formatDate = (dateString: string | Date) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg card-hover transition-all duration-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{displayTitle}</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {isExpanded ? 'Collapse' : 'View'}
            </Button>
          </div>
        </div>
        <CardDescription>
          Created {plan.createdAt ? formatDate(plan.createdAt) : 'Unknown'} • {trainingDays} training days
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Expanded Plan View */}
        {isExpanded && planData?.days && (
          <div className="mt-4 border-t pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {planData.days.map((day) => (
                <DayCard
                  key={day.day}
                  day={day}
                  onExerciseClick={(exercise) => {
                    setSelectedExercise(exercise);
                    setExerciseModalOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Coach Notes */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Coach Notes</span>
            <Button variant="ghost" size="sm" onClick={onAddNote}>
              <Plus className="w-3 h-3 mr-1" />
              Add
            </Button>
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-gray-400">No notes yet</p>
          ) : (
            <div className="space-y-2">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onEdit={() => onEditNote(note)}
                  onDelete={() => onDeleteNote(note.id)}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Exercise Modal */}
      <ExerciseModal
        isOpen={exerciseModalOpen}
        onClose={() => setExerciseModalOpen(false)}
        exercise={selectedExercise}
      />
    </Card>
  );
}

// Video Card Component
function VideoCard({
  video,
  notes,
  onAddNote,
  onEditNote,
  onDeleteNote,
}: {
  video: ExerciseVideoFeedback;
  notes: CoachNote[];
  onAddNote: () => void;
  onEditNote: (note: CoachNote) => void;
  onDeleteNote: (noteId: number) => void;
}) {
  const formatDate = (dateString: string | Date) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Card className="glass-card dark:glass-card-dark rounded-3xl shadow-lg overflow-hidden">
      {/* Video Thumbnail/Player Area */}
      <div className="relative aspect-video bg-gray-900">
        {video.videoUrl ? (
          <video
            src={video.videoUrl}
            className="w-full h-full object-cover"
            controls
            preload="metadata"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Play className="w-12 h-12 text-gray-500" />
          </div>
        )}
      </div>

      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold">{video.exerciseName || 'Exercise Video'}</h4>
          <Badge variant={video.feedbackJson ? 'default' : 'secondary'}>
            {video.feedbackJson ? 'Analyzed' : 'Pending'}
          </Badge>
        </div>
        <p className="text-sm text-gray-500 mb-3">{formatDate(video.createdAt)}</p>

        {/* AI Feedback Summary */}
        {video.feedbackJson && (
          <div className="glass-card dark:glass-card-dark rounded-2xl p-3 mb-3 border-l-4 border-[var(--apple-blue)]">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">AI Feedback</p>
            <p className="text-sm text-gray-700 dark:text-gray-200 line-clamp-3">
              {video.feedbackJson.exercise}: {video.feedbackJson.analysis?.biggest_error?.observation || 'Analysis complete'}
            </p>
          </div>
        )}

        {/* Coach Notes */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Coach Notes</span>
            <Button variant="ghost" size="sm" onClick={onAddNote}>
              <Plus className="w-3 h-3 mr-1" />
              Add
            </Button>
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-gray-400">No notes yet</p>
          ) : (
            <div className="space-y-2">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onEdit={() => onEditNote(note)}
                  onDelete={() => onDeleteNote(note.id)}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
